import * as React from 'react';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { CloudCheckIcon, CloudIcon, CloudWarningIcon } from '@phosphor-icons/react';
import { FetchWithVersionCheck } from '@/shared/functions';
import { AuthContext } from '@/app/contexts';

export interface SyncStatus {
  orgId: string;
  tenantId: string | null;
  tenantName: string | null;
  driveType: string;
  sourcePath: string;
  isEnabled: boolean;
  lastSyncStart: number | null;
  lastSyncEnd: number | null;
  lastFileCopiedTime: number | null;
  lastFileCopiedName: string | null;
}

type TenantRollupStatus = 'ENABLED' | 'DISABLED' | 'MIXED';

interface TenantRollup {
  tenantKey: string;
  tenantLabel: string;
  total: number;
  enabled: number;
  disabled: number;
  status: TenantRollupStatus;
  rows: SyncStatus[];
}

const tooltipClassNames = {
  content: 'bg-tooltip-bg text-white',
  base: 'before:bg-tooltip-bg',
};

/**
 * Fetches sync status from /api/protected/sync when enabled.
 * Returns syncStatus rows and a refetch function (e.g. for onMouseEnter).
 *
 * The access token is read through a ref so that token refreshes from
 * /api/init don't change fetchSyncStatus's identity and re-trigger the
 * mount effect — each refresh otherwise produced an extra GET per consumer.
 *
 * The effect also depends on a boolean `hasAuth` so the initial null →
 * populated transition triggers a fetch (otherwise an early enabled-flip
 * before AuthContext hydrates would 401 and leave syncStatus null until
 * a manual refetch). Subsequent refreshes don't flip the boolean, so the
 * extra-GET-per-refresh regression stays fixed.
 */
export function useSyncStatus(enabled: boolean) {
  const { accessToken, tenantName } = useContext(AuthContext);
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;
  const hasAuth = !!accessToken;
  // Bumped whenever the effect re-runs (logout, tenant switch, etc.) so an
  // in-flight fetch from the previous identity can't race a stale
  // setSyncStatus past the post-logout clear.
  const fetchGenerationRef = useRef(0);
  const [syncStatus, setSyncStatus] = useState<SyncStatus[] | null>(null);

  const fetchSyncStatus = useCallback(async () => {
    if (!accessTokenRef.current) {
      setSyncStatus(null);
      return;
    }
    const generation = fetchGenerationRef.current;
    try {
      const response = await FetchWithVersionCheck('/api/protected/sync', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessTokenRef.current}`,
        },
      });
      if (generation !== fetchGenerationRef.current) return;
      const data = await response.json();
      if (generation !== fetchGenerationRef.current) return;
      if (!response.ok || !data) {
        setSyncStatus(null);
        return;
      }
      if (data?.rows?.length) {
        setSyncStatus(data.rows);
      } else {
        setSyncStatus(null);
      }
    } catch {
      if (generation !== fetchGenerationRef.current) return;
      setSyncStatus(null);
    }
  }, []);

  useEffect(() => {
    fetchGenerationRef.current += 1;
    if (enabled && hasAuth) {
      // Clear before refetching on tenant change so the previous tenant's
      // rows can't render in the gap before the new fetch resolves. The
      // generation bump above already drops any in-flight tenant-A response.
      setSyncStatus(null);
      fetchSyncStatus();
    } else if (!hasAuth) {
      // Clear cached status when the user signs out so the next tenant
      // can't briefly see the previous tenant's sync icon between
      // accessToken clearing and component unmount.
      setSyncStatus(null);
    }
    // tenantName is in deps so /api/init tenant switches re-fetch (same
    // accessToken-truthy state, but new identity). Periodic 50-min token
    // refreshes don't change tenantName, so they don't re-fetch — the
    // accessTokenRef still picks up the new bearer for the next fetch.
  }, [enabled, hasAuth, tenantName, fetchSyncStatus]);

  return { syncStatus, refetch: fetchSyncStatus };
}

export interface SyncStatusIndicatorProps {
  syncStatus: SyncStatus[] | null;
  iconSize?: number;
  /** Rendered when syncStatus is disabled or empty (e.g. default cloud icon or placeholder) */
  fallback: React.ReactNode;
}

/**
 * Renders the sync status tooltip and icon (or fallback when no data).
 * Use with useSyncStatus() so sync logic lives in one place.
 */
export function SyncStatusIndicator({
  syncStatus,
  iconSize = 20,
  fallback,
}: SyncStatusIndicatorProps) {
  if (!syncStatus?.length) {
    return <>{fallback}</>;
  }

  const tenantRollups = syncStatus.reduce<TenantRollup[]>((acc, row) => {
    const tenantKey = row.tenantId ?? `org:${row.orgId}`;
    const tenantLabel = row.tenantName ?? (row.tenantId ? row.tenantId : 'Organization-level');
    const existing = acc.find((rollup) => rollup.tenantKey === tenantKey);
    if (existing) {
      existing.rows.push(row);
      existing.total += 1;
      if (row.isEnabled) {
        existing.enabled += 1;
      } else {
        existing.disabled += 1;
      }
      return acc;
    }

    acc.push({
      tenantKey,
      tenantLabel,
      total: 1,
      enabled: row.isEnabled ? 1 : 0,
      disabled: row.isEnabled ? 0 : 1,
      status: row.isEnabled ? 'ENABLED' : 'DISABLED',
      rows: [row],
    });
    return acc;
  }, []);

  tenantRollups.forEach((rollup) => {
    rollup.status =
      rollup.enabled > 0 && rollup.disabled > 0
        ? 'MIXED'
        : rollup.enabled > 0
          ? 'ENABLED'
          : 'DISABLED';
  });

  const content = (
    <div className="flex flex-col gap-2 p-2">
      {tenantRollups.map((tenant) => (
        <div key={tenant.tenantKey} className="p-2 border border-solid border-white/30">
          <div className="font-semibold">
            {tenant.tenantLabel}: {tenant.status}
          </div>
          <div>
            Enabled {tenant.enabled} / Disabled {tenant.disabled} / Total {tenant.total}
          </div>
          <div className="mt-2 flex flex-col gap-2">
            {tenant.rows.map((item, index) => (
              <div
                key={`${tenant.tenantKey}-${index}`}
                className={`p-2 border border-solid ${item.isEnabled ? 'border-green-500' : 'border-red-500'}`}
              >
                <div>
                  {item.driveType} - {item.sourcePath || 'No source path'}
                </div>
                <div>Status: {item.isEnabled ? 'ENABLED' : 'DISABLED'}</div>
                <div>
                  {item.lastSyncStart
                    ? `Last sync start: ${new Date(item.lastSyncStart * 1000).toLocaleString()}`
                    : 'No sync start'}
                </div>
                <div>
                  {item.lastSyncEnd
                    ? `Last sync end: ${new Date(item.lastSyncEnd * 1000).toLocaleString()}`
                    : 'No sync end'}
                </div>
                {item.lastFileCopiedTime != null && item.lastFileCopiedName && (
                  <div className="flex flex-col items-start justify-between gap-1">
                    <div>
                      Last copy: {new Date(item.lastFileCopiedTime * 1000).toLocaleString()}
                    </div>
                    <div>
                      File copied:{' '}
                      {item.lastFileCopiedName.slice(item.lastFileCopiedName.lastIndexOf('/') + 1)}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const tenantStatuses = tenantRollups.map((tenant) => tenant.status);
  const hasMixedTenants = tenantStatuses.includes('MIXED');
  const allTenantsEnabled = tenantStatuses.length > 0 && tenantStatuses.every((status) => status === 'ENABLED');
  const allTenantsDisabled = tenantStatuses.length > 0 && tenantStatuses.every((status) => status === 'DISABLED');

  const icon =
    allTenantsEnabled ? (
      <CloudCheckIcon size={iconSize} className="text-green-500" />
    ) : hasMixedTenants ? (
      <CloudWarningIcon size={iconSize} className="text-yellow-500" />
    ) : allTenantsDisabled ? (
      <CloudWarningIcon size={iconSize} className="text-red-500" />
    ) : (
      <CloudIcon size={iconSize} className="text-gray-500" />
    );

  const trigger = <div>{icon}</div>;

  return (
    <TpTooltip sideOffset={10} placement="left" content={content}>
      {trigger}
    </TpTooltip>
  );
}
